import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  calendarHourRange,
  calendarPieces,
  packLanes,
  type PlacedCalendarPiece,
  type ScheduleEntry,
} from "@edu/shared";
import { dayShort, minutesToTime } from "./common.ts";
import { CalendarDay } from "./CalendarDay.tsx";
import { calendarItems, type GridOpening } from "./openings.ts";
import { useCalendarDrag, type CalendarDrag } from "./use-calendar-drag.ts";

/**
 * Календарь колонками: дни рядом, часовая шкала слева, занятие стоит на своём
 * времени и своей длительности.
 *
 * Шесть решений, ни одно не косметическое.
 *
 * 1. **Диапазон часов ВЫВОДИТСЯ из занятий**, а не задан числом. Жёсткие 8-22
 *    однажды спрячут занятие в 7 утра, а сутки целиком дают шестнадцать пустых
 *    часов над содержимым. Рабочее окно по умолчанию расширяется до охвата
 *    всех занятий недели, поэтому спрятаться не может ни одно, и это свойство
 *    построения, а не проверка, которую можно забыть
 * 2. **Наложения делят ширину**, как в Outlook. Считает это `packLanes` из
 *    общего пакета, а не экран: раскладку дорожек надо было проверить прогоном,
 *    а прогон у нас живёт в `schedule-grid-check`
 * 3. **Фон колонки это кнопки по получасу**, а не один `div` с вычислением
 *    координаты нажатия. Так время попадания точное, а не выведенное из
 *    `offsetY`, и с клавиатуры экран остаётся проходимым. Двести кнопок на
 *    неделю браузеру ничего не стоят. ⚠️ С 10.09.2026 это решение окупается
 *    второй раз: те же кнопки стали целями переноса перетаскиванием, и дата с
 *    получасом приезжают туда готовыми
 * 4. **Занятие в колонке несёт только время и имя, и ни одной кнопки.** Высота
 *    коробки задана длительностью: занятие на полчаса это около тридцати
 *    пикселей, а при наложении двух занятий ширина колонки делится пополам.
 *    Кнопка «Войти» туда не влезает ни при какой вёрстке, поэтому и она, и
 *    причина отмены, и форма переноса живут в карточке РЯДОМ с занятием
 *    (`EntryPopover`), на узком экране снизу
 * 5. **Отметка «сейчас» это полоса поперёк сегодняшней колонки**, как в
 *    Outlook и Teams, плюс приглушённое прошлое. Без неё календарь отвечает на
 *    вопрос «что сегодня», но не на вопрос «что дальше»: человек ищет глазами
 *    ближайшее занятие, сверяясь с часами в углу экрана. Три свойства отметки
 *    обязательны, и каждое закрывает свою поломку. Минута приезжает готовой из
 *    `SchedulePage`, которая считает её по часам СЕРВЕРА, а не устройства
 *    (правило 3 того же экрана). Диапазон часов расширен так, чтобы накрыть её
 *    (`calendarHourRange` вторым доводом), иначе утром до первого занятия
 *    полоса оказалась бы над верхним краем. И полоса, и приглушение прошлого
 *    не ловят нажатия: пустое время под ними обязано заводить занятие
 * 6. **Календарь владеет своей высотой, и часы прокручиваются ВНУТРИ него**
 *    (17.09.2026). Экран расписания занимает ровно высоту окна и страницей не
 *    прокручивается вовсе; строка дней при этом стоит НАД прокручиваемым телом
 *    и липкой не является.
 *
 *    ⚠️ **Липкость тут была, работала и всё равно не решила задачу. Возвращать
 *    её нельзя, и историю стоит знать целиком.** Экран открывается прокрученным
 *    к текущему часу (раздел 9 проекта), и уехавшая вверх строка оставляла
 *    колонки без подписи. Первым ответом была липкость: правило
 *    `position: sticky` стояло у шапки давно и не срабатывало ни разу (ближайшей
 *    прокруткой был сам `.cal`), прокрутку вбок увезли на тело, и шапка
 *    прилипла по-настоящему, что подтвердил замер на телефоне владельца. Полоса
 *    календаря над ней осталась видна ВСЁ РАВНО: по числам замера она лежит
 *    примерно на минус 17 пикселях, то есть ВЫШЕ нулевой координаты окна, куда
 *    iOS Safari рисует страницу в зазоре под строкой состояния. Коробке с
 *    `top: 0` накрыть это нечем. Значит, пока мимо верхней кромки едет
 *    содержимое календаря, полоса видна всегда, и единственный ответ это не
 *    возить мимо кромки ничего.
 *
 *    ⚠️ Ручная синхронизация ниже от этого НЕ ушла: тело по-прежнему едет вбок
 *    (семь колонок в узкое окно не встают), и неподвижная шапка подписала бы
 *    колонки чужими днями. Шапка поэтому по-прежнему ТРИ коробки:
 *    `.cal__head` стоит и держит этаж, `.cal__head-clip` обрезает и ездит вбок,
 *    `.cal__head-row` несёт сетку колонок. Сам `.cal` при этом колонка
 *    (`display: flex`): высоту он получает снаружи и обязан раздать её строке
 *    дней и телу. Цепочка высоты и обязательный `min-height: 0` у каждого её
 *    звена разобраны в стилях у `.shell[data-fixed]`, сами правила у `.cal`,
 *    `.cal__head` и `.cal__body`
 *
 * ⚠️ Сама колонка уехала в `CalendarDay.tsx` 10.09.2026, а состояние жеста
 * переноса в `use-calendar-drag.ts`: файл стоял на 370 строках при потолке в
 * 400, а компонент на 262 при потолке в 150 (раздел 9 CLAUDE.md).
 */

export interface CalendarGridProps {
  entries: readonly ScheduleEntry[];
  /** Даты колонок, по возрастанию. Их число и есть режим экрана. */
  dates: readonly string[];
  /** Зона СМОТРЯЩЕГО: и колонки, и минуты считаются только в ней. */
  zone: string;
  /** Сегодня в зоне смотрящего, по часам СЕРВЕРА. */
  today: string;
  /**
   * Минуты от местной полуночи для отметки «сейчас», либо `null`, если
   * сегодняшнего дня среди колонок нет.
   *
   * ⚠️ Приезжает готовой, а не считается здесь по `Date.now()`. Часы на
   * устройстве бывают сбиты на десятки минут, и полоса «сейчас», нарисованная
   * по ним, обещала бы занятие, до которого ещё двадцать минут, либо прятала
   * бы то, что уже идёт. Это то же правило, по которому здесь не
   * пересчитывается состояние занятия.
   */
  nowMin: number | null;
  /**
   * Ключи занятий, в чате которых есть непрочитанное.
   *
   * ⚠️ **Приезжают готовым набором, а не считаются здесь**, и это не экономия.
   * Счётчик отвечает АДРЕСОМ чата (`pair:<слот>:<дата>` либо
   * `group:<группа>:<дата>`), а сетка знает ключ строки (`o:<слот>:<дата>`);
   * пространства имён разные, и перевод между ними живёт в одном месте
   * (`chatDotKeys` в `entry-chat.ts`), закрытом тестами. Второе его написание
   * поставило бы точку не на ту клетку молча.
   */
  chatDots: ReadonlySet<string>;
  selectedKey: string | null;
  /**
   * Нажатие по занятию. Вторым доводом едет сама коробка занятия: от неё
   * считается положение карточки, и в неё же возвращается фокус после закрытия.
   * Искать её потом по селектору значило бы завести второй способ адресовать
   * занятие, который разъедется с первым.
   */
  onSelect: (entry: ScheduleEntry, anchor: HTMLElement) => void;
  /**
   * Нажатие по пустому времени: дата колонки и `ЧЧ:ММ` получаса.
   *
   * ⚠️ `null` означает «смотреть можно, заводить нельзя», и это не блокировка
   * кнопок, а их ОТСУТСТВИЕ. Фон из двухсот отключённых кнопок с подписью
   * «Добавить занятие» читалка ученику прочитает всё равно, и это будет
   * обещание, которого сервер не выполнит.
   */
  onEmpty: ((date: string, time: string) => void) | null;
  /**
   * Перенос занятия перетаскиванием.
   *
   * ⚠️ `null` означает «жеста нет вовсе», и остаётся это ровно у ученика:
   * расписание ведёт преподаватель. Не отключённые обработчики, а их
   * отсутствие, ровно как у `onEmpty` строкой выше. Устройство тут ни при чём с
   * 12.09.2026: мышь берёт занятие сразу, палец удержанием.
   */
  drag: CalendarDrag | null;
  /**
   * Окна записи недели: свои и те, на которые можно записаться
   * (`docs/booking-design.md`, 3.2). Раскладываются вместе с занятиями, но не
   * перетаскиваются и во время жеста указатель не ловят.
   */
  openings: readonly GridOpening[];
  /** Нажатие по окну: вторым доводом коробка, как у занятия. */
  onSelectOpening: (opening: GridOpening, anchor: HTMLElement) => void;
}

/** Шаг сетки фона и округления нажатия. Полчаса это то, чем люди называют время. */
const STEP_MIN = 30;

/**
 * На сколько минут вокруг отметки «сейчас» гасится подпись часа.
 *
 * Двадцать две минуты, а не пять: подписи в шкале это две строки высотой в
 * пол-часа каждая, и наезжают они задолго до совпадения значений.
 */
const HOUR_LABEL_CLEAR_MIN = 22;

export function CalendarGrid(props: CalendarGridProps) {
  const { entries, dates, zone } = props;
  // По той же причине, что и раньше: сужение по `null` через свойство объекта
  // не переживает ни замыкание, ни соседнюю функцию
  const nowMin = props.nowMin;

  const byKey = useMemo(() => new Map(entries.map((e) => [e.key, e])), [entries]);

  /**
   * Куски и диапазон считаются по ВСЕМ занятиям окна, а не по видимым.
   *
   * Иначе диапазон часов прыгал бы при листании внутри недели: три дня без
   * ранних занятий давали бы 8:00, а соседние три дня 7:00, и строки уезжали бы
   * под курсором. Данные всё равно уже в памяти, экономить тут нечего.
   */
  /**
   * ⚠️ **Заглушка перенесённого занятия в календаре НЕ рисуется** (решение
   * владельца 10.09.2026). Полоска у края колонки на старом месте занимала
   * место и читалась как ещё одно занятие, а занятие в этот час уже не идёт.
   *
   * Цена названа прямо: у ученика, смотрящего на прежний час, не остаётся ни
   * следа переноса, и узнаёт он о нём, только увидев занятие на новом месте.
   * Там `перенесено с …` в карточке остаётся, как было.
   *
   * ⚠️ Отменённое занятие полоской остаётся, и это не непоследовательность:
   * отменённого занятия не будет вовсе, и исчезнувшее из сетки читается как
   * наша поломка (разбор в `docs/schedule-design.md`). Перенесённое же никуда
   * не делось, оно стоит в этой же неделе.
   */
  const shown = useMemo(() => entries.filter((e) => e.state !== "moved_away"), [entries]);
  const openings = props.openings;
  const openingsByKey = useMemo(() => new Map(openings.map((o) => [o.key, o])), [openings]);
  /*
    Окна входят и в диапазон часов, и в дорожки: иначе окно в 8:00 при
    занятиях с 14:00 стояло бы над верхним краем, а окно поверх занятия легло бы
    с ним внахлёст (3.2 проекта)
  */
  const pieces = useMemo(
    () => packLanes(calendarPieces(calendarItems(shown, openings), zone)),
    [openings, shown, zone],
  );
  const range = useMemo(() => calendarHourRange(pieces, nowMin), [pieces, nowMin]);

  const drag = useCalendarDrag(zone, props.drag);

  /**
   * Коробка с клипом ВНУТРИ шапки, а не сама шапка.
   *
   * ⚠️ Разведены они были предосторожностью против зазора над шапкой на iPhone,
   * а причина зазора оказалась другой (решение 6). Разводить обратно незачем:
   * одна коробка на одно дело читается лучше, и `scrollLeft` в любом случае
   * ставится тому, у кого стоит `overflow`, то есть клипу.
   */
  const headClipRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /**
   * Тело календаря нужно ДВУМ потребителям сразу, поэтому ссылка одна, а
   * кладётся узел в обе: своя (синхронизация шапки ниже) и `drag.calRef`, из
   * которого жест переноса двигает содержимое у края и берёт кромки. Два
   * атрибута `ref` на одном узле React не даёт, а выбор «кому достанется»
   * означал бы, что вторая половина молча получает `null`.
   */
  const setBody = useCallback(
    (el: HTMLDivElement | null) => {
      bodyRef.current = el;
      drag.calRef.current = el;
    },
    [drag.calRef],
  );

  /**
   * Подписи дней едут вбок за телом.
   *
   * Прокрутка вбок живёт у тела, а у шапки только клип, и лежит он на
   * ВЛОЖЕННОЙ `.cal__head-clip`: у самой `.cal__head` никакого `overflow` нет
   * (причина у правила в стилях). Двигается поэтому клип, и двигается
   * ОБЯЗАТЕЛЬНО: семь колонок в узкое окно не встают, тело уезжает вбок, а
   * неподвижные подписи назвали бы колонки чужими днями.
   *
   * ⚠️ `scrollLeft`, а не `transform`: клип и так делает коробку
   * прокручиваемой, и лишнего слоя композиции сдвиг не заводит.
   *
   * ⚠️ Синхронизация зовётся и СРАЗУ, без события: после смены числа колонок
   * браузер сам зажимает `scrollLeft` тела по новой ширине (семь колонок ужались
   * до одной, ехать больше некуда), и подписи обязаны приехать за ним.
   */
  useEffect(() => {
    const body = bodyRef.current;
    const clip = headClipRef.current;
    if (body === null || clip === null) return;
    const sync = () => {
      clip.scrollLeft = body.scrollLeft;
    };
    sync();
    body.addEventListener("scroll", sync, { passive: true });
    return () => {
      body.removeEventListener("scroll", sync);
    };
  }, [dates.length]);

  /**
   * Отступ отметки «сейчас» от верха календаря, строкой для `calc`.
   *
   * `null` означает «сегодня не на экране»: ни полосы, ни подписи в шкале
   * тогда нет вовсе. Прижимать её к краю в такой ситуации значило бы врать про
   * время в дне, который человек листает.
   */
  const nowTop =
    nowMin === null ? null : `calc(${String((nowMin - range.fromMin) / 60)} * var(--cal-hour))`;

  const rows: number[] = [];
  for (let m = range.fromMin; m < range.toMin; m += STEP_MIN) rows.push(m);

  const hours: number[] = [];
  for (let m = range.fromMin; m < range.toMin; m += 60) hours.push(m);

  const byDate = new Map<string, PlacedCalendarPiece[]>();
  for (const p of pieces) {
    const list = byDate.get(p.date);
    if (list === undefined) byDate.set(p.date, [p]);
    else list.push(p);
  }

  /** Высота колонки в часах: из неё же считаются отступы занятий. */
  const spanHours = (range.toMin - range.fromMin) / 60;

  /** Высота приглушённого прошлого у колонки, либо `null`, если день впереди. */
  const pastOf = (date: string): string | null => {
    if (date < props.today) return "100%";
    if (date !== props.today || nowMin === null) return null;
    return `calc(${String(Math.max(0, (nowMin - range.fromMin) / 60))} * var(--cal-hour))`;
  };

  return (
    <div
      className="cal"
      /*
        ⚠️ На время жеста коробки занятий перестают ловить указатель (правило в
        стилях). Иначе занятие нельзя положить туда, где уже стоит другое, а
        двигают именно туда: «поставлю сразу за Петей»
      */
      data-dragging={drag.dragging}
      style={
        {
          "--cal-span": String(spanHours),
          "--cal-cols": String(dates.length),
        } as React.CSSProperties
      }
    >
      <div className="cal__head">
        {/* Три коробки, а не две, и каждая держит ровно одно: `.cal__head`
            стоит над прокручиваемым телом и держит этаж, `.cal__head-clip`
            обрезает и ездит вбок по `scrollLeft`, `.cal__head-row` несёт сетку
            колонок. Разбор у правила `.cal__head` в стилях */}
        <div className="cal__head-clip" ref={headClipRef}>
          <div className="cal__head-row">
            <div className="cal__corner" aria-hidden="true" />
            {dates.map((date) => (
              <div key={date} className="cal__day" data-today={date === props.today}>
                {dayShort(date)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cal__body" ref={setBody}>
        <div className="cal__hours" aria-hidden="true">
          {hours.map((m) => (
            <div
              key={m}
              className="cal__hour"
              /*
                Подпись часа под отметкой «сейчас» убирается, а не рисуется
                поверх неё: две цифры в одной точке шкалы читаются как
                опечатка, и в 14:02 человек видел бы «14:00» и «14:02»
                внахлёст
              */
              data-hidden={nowMin !== null && Math.abs(m - nowMin) < HOUR_LABEL_CLEAR_MIN}
              style={{ top: `calc(${String((m - range.fromMin) / 60)} * var(--cal-hour))` }}
            >
              {minutesToTime(m)}
            </div>
          ))}

          {nowMin !== null && nowTop !== null && (
            <div className="cal__now-time" style={{ top: nowTop }}>
              {minutesToTime(nowMin)}
            </div>
          )}
        </div>

        {dates.map((date) => (
          <CalendarDay
            key={date}
            date={date}
            today={date === props.today}
            zone={zone}
            pieces={byDate.get(date) ?? []}
            byKey={byKey}
            openingsByKey={openingsByKey}
            onSelectOpening={props.onSelectOpening}
            range={range}
            rows={rows}
            nowMin={date === props.today ? nowMin : null}
            past={pastOf(date)}
            chatDots={props.chatDots}
            selectedKey={props.selectedKey}
            onSelect={props.onSelect}
            onEmpty={props.onEmpty}
            ghost={drag.ghost !== null && drag.ghost.date === date ? drag.ghost : null}
            boxProps={drag.boxProps}
          />
        ))}
      </div>
    </div>
  );
}
